import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import {
  useLaudos,
  useUsuarios,
  useParceirosByVistoriadora,
} from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  FileText,
  CheckCircle2,
  Clock,
  TrendingUp,
  Users,
  Building2,
  Car,
  BarChart2,
  ArrowUpRight,
  Download,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import {
  LAUDO_STATUS_LABELS,
  LAUDO_STATUS_COLORS,
  MONTH_NAMES,
  VEICULO_TIPO_LABELS,
} from "@/lib/constants";

// ── helpers ────────────────────────────────────────────────────────────────────

const MESES_ABREV = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const STATUS_ORDER = [
  "rascunho",
  "enviado_analise",
  "em_vistoria",
  "em_avaliacao_rt",
  "assinado",
  "finalizado",
];

const TIPOS_VEICULO = ["auto_leve", "moto", "caminhao", "onibus"] as const;

function pctStr(value: number, total: number): string {
  if (!total) return "—";
  return `${Math.round((value / total) * 100)}%`;
}

function toDate(val: unknown): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  const d = new Date(val as string);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(val: unknown): string {
  const d = toDate(val);
  return d ? d.toLocaleDateString("pt-BR") : "—";
}

// ── PDF export ─────────────────────────────────────────────────────────────────

interface ReportData {
  empresa: string;
  ano: number;
  total: number;
  finalizados: number;
  emAndamento: number;
  byStatus: { status: string; count: number }[];
  byMonth: { mes: string; total: number }[];
  byTecnico: { nome: string; total: number; finalizados: number; emAndamento: number }[];
  byParceiro: { nome: string; total: number; finalizados: number }[];
  byVeiculo: { nome: string; total: number; finalizados: number }[];
}

function buildPrintHTML(data: ReportData): string {
  const pct = (v: number, t: number) => (!t ? "—" : `${Math.round((v / t) * 100)}%`);
  const bar = (v: number, t: number) => {
    const w = !t ? 0 : Math.round((v / t) * 100);
    return `<div style="background:#e2e8f0;border-radius:4px;height:8px;width:100%"><div style="background:#22c55e;border-radius:4px;height:8px;width:${w}%"></div></div>`;
  };
  const tableStyle = `border-collapse:collapse;width:100%;font-size:13px;margin-bottom:24px`;
  const thStyle = `border:1px solid #cbd5e1;padding:8px 12px;background:#f8fafc;text-align:left;font-weight:600;color:#475569`;
  const tdStyle = `border:1px solid #cbd5e1;padding:8px 12px;color:#1e293b`;
  const tdR = `${tdStyle};text-align:right`;

  const monthBars = data.byMonth.map((m) => {
    const h = data.byMonth.reduce((mx, x) => Math.max(mx, x.total), 0);
    const pctH = h ? Math.round((m.total / h) * 100) : 0;
    return `<div style="display:flex;flex-direction:column;align-items:center;gap:4px;flex:1">
      <span style="font-size:11px;font-weight:600;color:#1e293b">${m.total || ""}</span>
      <div style="width:100%;background:#e2e8f0;border-radius:4px 4px 0 0;display:flex;flex-direction:column;justify-content:flex-end;height:80px">
        <div style="background:#6366f1;border-radius:4px 4px 0 0;height:${pctH}%"></div>
      </div>
      <span style="font-size:10px;color:#64748b">${m.mes}</span>
    </div>`;
  }).join("");

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Relatório LaudoLegal — ${data.empresa} — ${data.ano}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, Arial, sans-serif; color: #1e293b; padding: 32px; font-size: 14px; }
  @media print {
    body { padding: 16px; }
    .no-print { display: none !important; }
    h2 { page-break-before: always; }
    h2:first-of-type { page-break-before: avoid; }
  }
  h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
  h2 { font-size: 16px; font-weight: 600; margin: 24px 0 12px; color: #334155; border-bottom: 2px solid #e2e8f0; padding-bottom: 6px; }
  .meta { color: #64748b; font-size: 13px; margin-bottom: 24px; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
  .kpi { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
  .kpi-label { font-size: 12px; color: #64748b; margin-bottom: 4px; }
  .kpi-value { font-size: 26px; font-weight: 700; }
  .kpi-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
  .status-badges { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px; }
  .status-badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; background: #f1f5f9; color: #475569; }
  .month-chart { display: flex; gap: 4px; align-items: flex-end; margin-bottom: 24px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
  .print-btn { background:#6366f1;color:#fff;border:none;padding:10px 20px;border-radius:8px;font-size:14px;cursor:pointer;margin-bottom:24px; }
</style>
</head>
<body>
<button class="no-print print-btn" onclick="window.print()">Imprimir / Salvar PDF</button>
<h1>Relatório de Laudos</h1>
<p class="meta">${data.empresa} &nbsp;|&nbsp; Ano: ${data.ano} &nbsp;|&nbsp; Gerado em: ${new Date().toLocaleString("pt-BR")}</p>

<h2>Visão Geral</h2>
<div class="kpis">
  <div class="kpi"><div class="kpi-label">Total de Laudos</div><div class="kpi-value">${data.total}</div></div>
  <div class="kpi"><div class="kpi-label">Finalizados</div><div class="kpi-value">${data.finalizados}</div><div class="kpi-sub">${pct(data.finalizados, data.total)} do total</div></div>
  <div class="kpi"><div class="kpi-label">Em Andamento</div><div class="kpi-value">${data.emAndamento}</div></div>
  <div class="kpi"><div class="kpi-label">Taxa de Conclusão</div><div class="kpi-value">${pct(data.finalizados, data.total)}</div></div>
</div>

<div class="status-badges">
  ${data.byStatus.map((s) => `<span class="status-badge">${LAUDO_STATUS_LABELS[s.status] ?? s.status}: ${s.count} (${pct(s.count, data.total)})</span>`).join("")}
</div>

<p style="font-size:12px;color:#64748b;margin-bottom:8px">Laudos criados por mês — ${data.ano}</p>
<div class="month-chart">${monthBars}</div>

${data.byTecnico.length > 0 ? `
<h2>Por Técnico</h2>
<table style="${tableStyle}">
  <thead><tr>
    <th style="${thStyle}">Técnico</th>
    <th style="${thStyle};text-align:right">Total</th>
    <th style="${thStyle};text-align:right">Finalizados</th>
    <th style="${thStyle};text-align:right">Em Andamento</th>
    <th style="${thStyle};text-align:right">% Conclusão</th>
  </tr></thead>
  <tbody>
    ${data.byTecnico.map((t) => `<tr>
      <td style="${tdStyle}">${t.nome}</td>
      <td style="${tdR}">${t.total}</td>
      <td style="${tdR};color:#15803d">${t.finalizados}</td>
      <td style="${tdR};color:#d97706">${t.emAndamento}</td>
      <td style="${tdR}">${pct(t.finalizados, t.total)}</td>
    </tr>`).join("")}
  </tbody>
</table>` : ""}

${data.byParceiro.length > 0 ? `
<h2>Por Parceiro</h2>
<table style="${tableStyle}">
  <thead><tr>
    <th style="${thStyle}">Parceiro</th>
    <th style="${thStyle};text-align:right">Total</th>
    <th style="${thStyle};text-align:right">Finalizados</th>
    <th style="${thStyle};text-align:right">% Conclusão</th>
    <th style="${thStyle}">Progresso</th>
  </tr></thead>
  <tbody>
    ${data.byParceiro.map((p) => `<tr>
      <td style="${tdStyle}">${p.nome}</td>
      <td style="${tdR}">${p.total}</td>
      <td style="${tdR};color:#15803d">${p.finalizados}</td>
      <td style="${tdR}">${pct(p.finalizados, p.total)}</td>
      <td style="${tdStyle};min-width:120px">${bar(p.finalizados, p.total)}</td>
    </tr>`).join("")}
  </tbody>
</table>` : ""}

<h2>Por Tipo de Veículo</h2>
<table style="${tableStyle}">
  <thead><tr>
    <th style="${thStyle}">Tipo</th>
    <th style="${thStyle};text-align:right">Total</th>
    <th style="${thStyle};text-align:right">Finalizados</th>
    <th style="${thStyle};text-align:right">% Conclusão</th>
    <th style="${thStyle}">Progresso</th>
  </tr></thead>
  <tbody>
    ${data.byVeiculo.map((v) => `<tr>
      <td style="${tdStyle}">${v.nome}</td>
      <td style="${tdR}">${v.total}</td>
      <td style="${tdR};color:#15803d">${v.finalizados}</td>
      <td style="${tdR}">${pct(v.finalizados, v.total)}</td>
      <td style="${tdStyle};min-width:120px">${bar(v.finalizados, v.total)}</td>
    </tr>`).join("")}
  </tbody>
</table>
</body>
</html>`;
}

function exportarPDF(data: ReportData) {
  const html = buildPrintHTML(data);
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

// ── sub-components ─────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  colorClass,
  loading,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  colorClass: string;
  loading: boolean;
}) {
  return (
    <Card className="border-none shadow-md">
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${colorClass} shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            {loading ? (
              <Skeleton className="h-8 w-20 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-slate-800">{value}</p>
            )}
            {sub && !loading && (
              <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <BarChart2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <p>{message}</p>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function Relatorios() {
  const { profile } = useAuth();
  const [, navigate] = useLocation();
  const empresaId = profile?.empresa?.id ?? profile?.empresaId ?? "";
  const empresaNome =
    profile?.empresa?.nomeFantasia ?? profile?.empresa?.razaoSocial ?? "Vistoriadora";

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Detalhado tab filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMes, setFilterMes] = useState("all");
  const [filterTecnico, setFilterTecnico] = useState("all");
  const [filterParceiro, setFilterParceiro] = useState("all");
  const [filterTipoVeiculo, setFilterTipoVeiculo] = useState("all");

  // Data
  const { data: laudos, isLoading } = useLaudos(
    empresaId,
    undefined,
    undefined,
    selectedYear,
  );
  const { data: usuarios } = useUsuarios(empresaId);
  const { data: parceirosData } = useParceirosByVistoriadora(empresaId);

  const allLaudos = laudos ?? [];
  const tecnicos = (usuarios ?? []).filter((u) => u.roles?.includes("tv"));

  // ── Aggregations ─────────────────────────────────────────────────────────────

  const total = allLaudos.length;
  const finalizados = allLaudos.filter((l) => l.status === "finalizado").length;
  const emAndamento = allLaudos.filter((l) =>
    ["enviado_analise", "em_vistoria", "em_avaliacao_rt"].includes(l.status ?? ""),
  ).length;

  // Monthly chart
  const byMonth = useMemo(
    () =>
      MESES_ABREV.map((mes, i) => ({
        mes,
        total: allLaudos.filter((l) => {
          const d = toDate(l.criadoEm);
          return d && d.getMonth() === i && d.getFullYear() === selectedYear;
        }).length,
      })),
    [allLaudos, selectedYear],
  );

  // Status breakdown
  const byStatus = useMemo(() => {
    const map: Record<string, number> = {};
    for (const l of allLaudos) {
      const s = l.status ?? "rascunho";
      map[s] = (map[s] ?? 0) + 1;
    }
    return STATUS_ORDER.filter((s) => map[s]).map((s) => ({
      status: s,
      count: map[s]!,
    }));
  }, [allLaudos]);

  // By technician — uses tecnicoVistoria join returned by the API
  const byTecnico = useMemo(() => {
    const map: Record<
      string,
      { nome: string; total: number; finalizados: number; emAndamento: number }
    > = {};
    for (const l of allLaudos) {
      const id = l.tecnicoVistoriaId ?? "__none__";
      const nome = (l as any).tecnicoVistoria?.nome ?? "Sem técnico";
      if (!map[id]) map[id] = { nome, total: 0, finalizados: 0, emAndamento: 0 };
      map[id].total++;
      if (l.status === "finalizado") map[id].finalizados++;
      if (["enviado_analise", "em_vistoria", "em_avaliacao_rt"].includes(l.status ?? ""))
        map[id].emAndamento++;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [allLaudos]);

  // By partner — uses parceiro join returned by the API
  const byParceiro = useMemo(() => {
    const map: Record<
      string,
      { nome: string; total: number; finalizados: number }
    > = {};
    for (const l of allLaudos) {
      const id = l.parceiroId ?? "__none__";
      const p = (l as any).parceiro;
      const nome = p?.nomeFantasia ?? p?.razaoSocial ?? "Sem parceiro";
      if (!map[id]) map[id] = { nome, total: 0, finalizados: 0 };
      map[id].total++;
      if (l.status === "finalizado") map[id].finalizados++;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [allLaudos]);

  // By vehicle type — uses veiculo join already present
  const byVeiculo = useMemo(
    () =>
      TIPOS_VEICULO.map((tipo) => {
        const group = allLaudos.filter((l) => (l as any).veiculo?.tipo === tipo);
        return {
          tipo,
          nome: VEICULO_TIPO_LABELS[tipo] ?? tipo,
          total: group.length,
          finalizados: group.filter((l) => l.status === "finalizado").length,
        };
      }),
    [allLaudos],
  );

  // Filtered for Detalhado tab
  const laudosDetalhado = useMemo(
    () =>
      allLaudos.filter((l) => {
        if (filterStatus !== "all" && l.status !== filterStatus) return false;
        if (filterMes !== "all") {
          const d = toDate(l.criadoEm);
          if (!d || d.getMonth() + 1 !== Number(filterMes)) return false;
        }
        if (filterTecnico !== "all" && (l.tecnicoVistoriaId ?? "__none__") !== filterTecnico)
          return false;
        if (filterParceiro !== "all" && (l.parceiroId ?? "__none__") !== filterParceiro)
          return false;
        if (filterTipoVeiculo !== "all" && (l as any).veiculo?.tipo !== filterTipoVeiculo)
          return false;
        return true;
      }),
    [allLaudos, filterStatus, filterMes, filterTecnico, filterParceiro, filterTipoVeiculo],
  );

  const years = Array.from({ length: 4 }, (_, i) => currentYear - i);

  // PDF export data
  const reportData: ReportData = {
    empresa: empresaNome,
    ano: selectedYear,
    total,
    finalizados,
    emAndamento,
    byStatus,
    byMonth,
    byTecnico,
    byParceiro,
    byVeiculo,
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Shell>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1
            className="text-3xl font-bold font-display text-slate-900"
            data-testid="text-page-title"
          >
            Relatórios
          </h1>
          <p className="text-muted-foreground mt-1">
            Análise e desempenho da vistoriadora
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(selectedYear)}
            onValueChange={(v) => setSelectedYear(Number(v))}
          >
            <SelectTrigger className="w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => exportarPDF(reportData)}
            disabled={isLoading || total === 0}
          >
            <Download className="w-4 h-4" />
            Exportar PDF
          </Button>
        </div>
      </div>

      <Tabs defaultValue="visao-geral">
        <TabsList className="mb-6 h-auto flex-wrap gap-1">
          <TabsTrigger value="visao-geral">Visão Geral</TabsTrigger>
          <TabsTrigger value="por-tecnico">Por Técnico</TabsTrigger>
          <TabsTrigger value="por-parceiro">Por Parceiro</TabsTrigger>
          <TabsTrigger value="por-veiculo">Por Veículo</TabsTrigger>
          <TabsTrigger value="detalhado">Detalhado</TabsTrigger>
        </TabsList>

        {/* ── Visão Geral ── */}
        <TabsContent value="visao-geral">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Total de Laudos"
              value={total}
              icon={FileText}
              colorClass="bg-blue-100 text-blue-600"
              loading={isLoading}
            />
            <KpiCard
              label="Finalizados"
              value={finalizados}
              sub={`${pctStr(finalizados, total)} do total`}
              icon={CheckCircle2}
              colorClass="bg-green-100 text-green-600"
              loading={isLoading}
            />
            <KpiCard
              label="Em Andamento"
              value={emAndamento}
              icon={Clock}
              colorClass="bg-amber-100 text-amber-600"
              loading={isLoading}
            />
            <KpiCard
              label="Taxa de Conclusão"
              value={pctStr(finalizados, total)}
              icon={TrendingUp}
              colorClass="bg-purple-100 text-purple-600"
              loading={isLoading}
            />
          </div>

          <Card className="border-none shadow-md mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Laudos criados por mês — {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-44 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={176}>
                  <BarChart
                    data={byMonth}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={32} />
                    <Tooltip
                      formatter={(v: number) => [v, "Laudos"]}
                      labelStyle={{ fontWeight: 600 }}
                    />
                    <Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="border-none shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Distribuição por status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex gap-3 flex-wrap">
                  {Array(5).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-36 rounded-xl" />
                  ))}
                </div>
              ) : byStatus.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem laudos no período.</p>
              ) : (
                <div className="flex gap-3 flex-wrap">
                  {byStatus.map(({ status, count }) => (
                    <div
                      key={status}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${LAUDO_STATUS_COLORS[status] ?? "bg-slate-100 text-slate-700"}`}
                    >
                      <span>{LAUDO_STATUS_LABELS[status] ?? status}</span>
                      <span className="font-bold">{count}</span>
                      <span className="opacity-60">({pctStr(count, total)})</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Por Técnico ── */}
        <TabsContent value="por-tecnico">
          <Card className="border-none shadow-md mb-4">
            <CardHeader className="flex flex-row items-center gap-2 pb-3">
              <Users className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Produtividade por técnico — {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="space-y-3 p-6">
                  {Array(4).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-12 rounded-lg" />
                  ))}
                </div>
              ) : byTecnico.length === 0 ? (
                <Empty message="Nenhum dado de técnico disponível." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Técnico</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Finalizados</TableHead>
                      <TableHead className="text-right">Em Andamento</TableHead>
                      <TableHead className="text-right">% Conclusão</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byTecnico.map((t) => (
                      <TableRow key={t.nome}>
                        <TableCell className="font-medium">{t.nome}</TableCell>
                        <TableCell className="text-right">{t.total}</TableCell>
                        <TableCell className="text-right text-green-700">{t.finalizados}</TableCell>
                        <TableCell className="text-right text-amber-600">{t.emAndamento}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {pctStr(t.finalizados, t.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {byTecnico.length > 0 && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Comparativo por técnico (top 10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={byTecnico.slice(0, 10)}
                    margin={{ top: 4, right: 8, left: 0, bottom: 48 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="nome"
                      tick={{ fontSize: 11 }}
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={32} />
                    <Tooltip formatter={(v: number) => [v, ""]} />
                    <Legend wrapperStyle={{ paddingTop: 8 }} />
                    <Bar dataKey="total" name="Total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="finalizados" name="Finalizados" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Por Parceiro ── */}
        <TabsContent value="por-parceiro">
          <Card className="border-none shadow-md mb-4">
            <CardHeader className="flex flex-row items-center gap-2 pb-3">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Laudos por parceiro — {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="space-y-3 p-6">
                  {Array(4).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-12 rounded-lg" />
                  ))}
                </div>
              ) : byParceiro.length === 0 ? (
                <Empty message="Nenhum dado de parceiro disponível." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parceiro</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Finalizados</TableHead>
                      <TableHead className="text-right">% Conclusão</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byParceiro.map((p) => (
                      <TableRow key={p.nome}>
                        <TableCell className="font-medium">{p.nome}</TableCell>
                        <TableCell className="text-right">{p.total}</TableCell>
                        <TableCell className="text-right text-green-700">{p.finalizados}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {pctStr(p.finalizados, p.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {byParceiro.length > 0 && (
            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Volume por parceiro (top 10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={byParceiro.slice(0, 10)}
                    margin={{ top: 4, right: 8, left: 0, bottom: 48 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="nome"
                      tick={{ fontSize: 11 }}
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={32} />
                    <Tooltip formatter={(v: number) => [v, ""]} />
                    <Legend wrapperStyle={{ paddingTop: 8 }} />
                    <Bar dataKey="total" name="Total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="finalizados" name="Finalizados" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Por Veículo ── */}
        <TabsContent value="por-veiculo">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {byVeiculo.map(({ tipo, nome, total: t, finalizados: f }) => (
              <Card key={tipo} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <Car className="w-5 h-5 text-slate-600" />
                    </div>
                    <p className="font-semibold text-slate-800">{nome}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{t}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{f}</p>
                      <p className="text-xs text-muted-foreground">Finalizados</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-700">{pctStr(f, t)}</p>
                      <p className="text-xs text-muted-foreground">Conclusão</p>
                    </div>
                  </div>
                  {t > 0 && (
                    <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((f / t) * 100)}%` }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-none shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Comparativo por tipo de veículo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart
                    data={byVeiculo}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="nome" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={32} />
                    <Tooltip formatter={(v: number) => [v, ""]} />
                    <Legend wrapperStyle={{ paddingTop: 8 }} />
                    <Bar dataKey="total" name="Total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="finalizados" name="Finalizados" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Detalhado ── */}
        <TabsContent value="detalhado">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>
                    {LAUDO_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterMes} onValueChange={setFilterMes}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os meses</SelectItem>
                {MONTH_NAMES.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterTecnico} onValueChange={setFilterTecnico}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Técnico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os técnicos</SelectItem>
                {tecnicos.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterParceiro} onValueChange={setFilterParceiro}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Parceiro" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os parceiros</SelectItem>
                {(parceirosData ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nomeFantasia ?? p.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterTipoVeiculo} onValueChange={setFilterTipoVeiculo}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="auto_leve">Auto / Leve</SelectItem>
                <SelectItem value="moto">Moto</SelectItem>
                <SelectItem value="caminhao">Caminhão</SelectItem>
                <SelectItem value="onibus">Ônibus</SelectItem>
              </SelectContent>
            </Select>

            <span className="ml-auto text-sm text-muted-foreground">
              {laudosDetalhado.length} resultado
              {laudosDetalhado.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Table */}
          <Card className="border-none shadow-md">
            <CardContent className="p-0">
              {isLoading ? (
                <div className="space-y-3 p-6">
                  {Array(6).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-12 rounded-lg" />
                  ))}
                </div>
              ) : laudosDetalhado.length === 0 ? (
                <div className="py-16">
                  <Empty message="Nenhum laudo encontrado com os filtros selecionados." />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Laudo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Parceiro</TableHead>
                      <TableHead>Técnico</TableHead>
                      <TableHead>Veículo</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {laudosDetalhado.map((l) => {
                      const lAny = l as any;
                      const parceiroNome =
                        lAny.parceiro?.nomeFantasia ?? lAny.parceiro?.razaoSocial ?? "—";
                      const tecnicoNome = lAny.tecnicoVistoria?.nome ?? "—";
                      const veiculo = lAny.veiculo;
                      const veiculoDesc = veiculo
                        ? (`${veiculo.marca ?? ""} ${veiculo.modelo ?? ""}`.trim() || veiculo.placa || "—")
                        : "—";
                      return (
                        <TableRow
                          key={l.id}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => navigate(`/laudos/${l.id}`)}
                        >
                          <TableCell className="font-mono text-sm text-slate-500">
                            {l.laudoId}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={LAUDO_STATUS_COLORS[l.status ?? "rascunho"]}
                            >
                              {LAUDO_STATUS_LABELS[l.status ?? "rascunho"]}
                            </Badge>
                          </TableCell>
                          <TableCell>{parceiroNome}</TableCell>
                          <TableCell>{tecnicoNome}</TableCell>
                          <TableCell className="text-sm">{veiculoDesc}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(l.criadoEm)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-7 h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/laudos/${l.id}`);
                              }}
                            >
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
